import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { resumeAPI, filterAPI, emailAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import Modal from '../components/Modal';
import toast from 'react-hot-toast';
import { Search, Filter, Mail, ChevronDown, ChevronUp, X, ArrowUpDown, FileText, Send } from 'lucide-react';

export default function SearchPage() {
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const searchTimeout = useRef(null);

  const [resumes, setResumes] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [filterOptions, setFilterOptions] = useState(null);
  const [loading, setLoading] = useState(true);

  const [query, setQuery] = useState(searchParams.get('q') || '');
  const [fieldsOnly, setFieldsOnly] = useState(searchParams.get('fields_only') === 'true');
  const [filters, setFilters] = useState({
    visastatus: searchParams.get('visastatus') || '',
    employer: searchParams.get('employer') || '',
    city: searchParams.get('city') || '',
    state: searchParams.get('state') || '',
    country: searchParams.get('country') || '',
    relocation: searchParams.get('relocation') || '',
    availablty: searchParams.get('availablty') || '',
    vendorname: searchParams.get('vendorname') || '',
    condition: searchParams.get('condition') || '',
    hold1: searchParams.get('hold1') || '',
  });
  const [showFilters, setShowFilters] = useState(false);
  const [page, setPage] = useState(parseInt(searchParams.get('page')) || 1);
  const [ordering, setOrdering] = useState(searchParams.get('ordering') || '-recno');

  const [selected, setSelected] = useState(new Set());
  const [selectAll, setSelectAll] = useState(false);
  const [emailModal, setEmailModal] = useState(false);
  const [emailAllModal, setEmailAllModal] = useState(false);
  const [emailSubject, setEmailSubject] = useState('');
  const [emailBody, setEmailBody] = useState('');
  const [sendingEmail, setSendingEmail] = useState(false);
  const [allEmailInfo, setAllEmailInfo] = useState(null);
  const [loadingAllEmails, setLoadingAllEmails] = useState(false);

  useEffect(() => { filterAPI.options().then(({ data }) => setFilterOptions(data)).catch(() => {}); }, []);

  const buildParams = useCallback(() => {
    const params = { page, ordering };
    if (query) params.q = query;
    if (fieldsOnly) params.fields_only = 'true';
    Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
    return params;
  }, [query, fieldsOnly, filters, page, ordering]);

  const fetchResumes = useCallback(async () => {
    if (!query) {
      setResumes([]);
      setTotalCount(0);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const { data } = await resumeAPI.list(buildParams());
      setResumes(data.results || []);
      setTotalCount(data.count || 0);
    } catch { toast.error('Failed to fetch resumes'); }
    setLoading(false);
  }, [buildParams, query]);

  useEffect(() => { fetchResumes(); }, [fetchResumes]);

  useEffect(() => {
    const params = {};
    if (query) params.q = query;
    if (fieldsOnly) params.fields_only = 'true';
    if (page > 1) params.page = page;
    if (ordering !== '-recno') params.ordering = ordering;
    Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
    setSearchParams(params, { replace: true });
  }, [query, fieldsOnly, filters, page, ordering, setSearchParams]);

  const debouncedSearch = (value) => {
    clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(() => { setQuery(value); setPage(1); }, 400);
  };

  const handleFieldsOnlyToggle = (checked) => {
    setFieldsOnly(checked);
    setPage(1);
  };

  const handleFilterChange = (key, value) => { setFilters(prev => ({ ...prev, [key]: value })); setPage(1); };
  const clearFilters = () => { setFilters({ visastatus: '', employer: '', city: '', state: '', country: '', relocation: '', availablty: '', vendorname: '', condition: '', hold1: '' }); setQuery(''); setFieldsOnly(false); setPage(1); };
  const toggleSort = (field) => setOrdering(prev => prev === field ? `-${field}` : prev === `-${field}` ? field : field);

  const toggleSelect = (recno) => setSelected(prev => { const n = new Set(prev); n.has(recno) ? n.delete(recno) : n.add(recno); return n; });
  const toggleSelectAll = () => { if (selectAll) { setSelected(new Set()); } else { setSelected(new Set(resumes.map(r => r.recno))); } setSelectAll(!selectAll); };

  // Send email to selected candidates
  const handleSendEmail = async () => {
    if (!emailSubject.trim() || !emailBody.trim()) { toast.error('Subject and body required'); return; }
    setSendingEmail(true);
    try {
      const { data } = await emailAPI.send({ subject: emailSubject, body: emailBody, recnos: Array.from(selected), bcc: ["rahulkj@yahoo.com"] });
      toast.success(data.message);
      setEmailModal(false); setEmailSubject(''); setEmailBody(''); setSelected(new Set()); setSelectAll(false);
    } catch (err) { toast.error(err.response?.data?.error || 'Failed to send email'); }
    setSendingEmail(false);
  };

  // Fetch all emails for current search, then open modal
  const handleEmailAllResults = async () => {
    setLoadingAllEmails(true);
    try {
      const { data } = await resumeAPI.exportEmails(buildParams());
      setAllEmailInfo(data);
      setEmailAllModal(true);
    } catch { toast.error('Failed to fetch email list'); }
    setLoadingAllEmails(false);
  };

  // Send email to ALL search results
  const handleSendEmailAll = async () => {
    if (!emailSubject.trim() || !emailBody.trim()) { toast.error('Subject and body required'); return; }
    if (!allEmailInfo?.recnos?.length) { toast.error('No eligible recipients'); return; }
    setSendingEmail(true);
    try {
      const { data } = await emailAPI.send({ subject: emailSubject, body: emailBody, recnos: allEmailInfo.recnos, bcc: ["rahulkj@yahoo.com"] });
      toast.success(data.message);
      setEmailAllModal(false); setEmailSubject(''); setEmailBody(''); setAllEmailInfo(null);
    } catch (err) { toast.error(err.response?.data?.error || 'Failed to send email'); }
    setSendingEmail(false);
  };

  const totalPages = Math.ceil(totalCount / 25);
  const activeFilterCount = Object.values(filters).filter(v => v).length;

  const FilterSelect = ({ label, filterKey, optionsKey }) => (
    <div className="filter-group">
      <label>{label}</label>
      <select value={filters[filterKey]} onChange={e => handleFilterChange(filterKey, e.target.value)}>
        <option value="">All</option>
        {(filterOptions?.[optionsKey] || []).map(v => <option key={v} value={v}>{typeof v === 'string' ? v.trim() : v}</option>)}
      </select>
    </div>
  );

  return (
    <div className="page-container">
      <div className="page-header">
        <div><h1 className="page-title">Search Candidates</h1><p className="page-subtitle">{totalCount.toLocaleString()} resumes in database</p></div>
        {isAdmin && <button className="btn btn-primary" onClick={() => navigate('/resumes/new')}><FileText size={16} /> Add Resume</button>}
      </div>

      {/* Search */}
      <div className="search-bar">
        <Search className="search-icon" />
        <input placeholder={fieldsOnly ? "Search by name, employer, location, visa..." : "Search in resume text..."} defaultValue={query} onChange={e => debouncedSearch(e.target.value)} />
        {totalCount > 0 && <span className="search-count">{totalCount.toLocaleString()}</span>}
      </div>

      {/* Search in fields only checkbox */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20, marginTop: -12 }}>
        <input
          type="checkbox"
          id="fieldsOnly"
          checked={fieldsOnly}
          onChange={e => handleFieldsOnlyToggle(e.target.checked)}
          style={{ width: 16, height: 16, accentColor: 'var(--accent)', cursor: 'pointer' }}
        />
        <label htmlFor="fieldsOnly" style={{ fontSize: '.85rem', color: 'var(--text-secondary)', cursor: 'pointer', userSelect: 'none' }}>
          Search name &amp; fields only <span style={{ fontSize: '.78rem', color: 'var(--text-muted)' }}>(faster — skips resume text)</span>
        </label>
      </div>

      {/* Toolbar */}
      <div className="toolbar">
        <div className="toolbar-left">
          <button className="btn btn-secondary btn-sm" onClick={() => setShowFilters(!showFilters)}>
            <Filter size={14} /> Filters {activeFilterCount > 0 && `(${activeFilterCount})`}
            {showFilters ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
          </button>
          {activeFilterCount > 0 && <button className="btn btn-ghost btn-sm" onClick={clearFilters}><X size={14} /> Clear</button>}

          {/* Email All Results button */}
          {totalCount > 0 && (
            <button className="btn btn-secondary btn-sm" onClick={handleEmailAllResults} disabled={loadingAllEmails}>
              <Send size={14} /> {loadingAllEmails ? 'Loading...' : `Email All Results`}
            </button>
          )}
        </div>
        <div className="toolbar-right">
          {selected.size > 0 && (<>
            <span className="selected-count">{selected.size} selected</span>
            <button className="btn btn-primary btn-sm" onClick={() => setEmailModal(true)}><Mail size={14} /> Email Selected</button>
            <button className="btn btn-ghost btn-sm" onClick={() => { setSelected(new Set()); setSelectAll(false); }}><X size={14} /></button>
          </>)}
        </div>
      </div>

      {/* Filters Panel */}
      {showFilters && (
        <div className="filters-panel">
          <FilterSelect label="Visa Status" filterKey="visastatus" optionsKey="visa_statuses" />
          <FilterSelect label="Employer" filterKey="employer" optionsKey="employers" />
          <FilterSelect label="City" filterKey="city" optionsKey="cities" />
          <FilterSelect label="State" filterKey="state" optionsKey="states" />
          <FilterSelect label="Country" filterKey="country" optionsKey="countries" />
          <FilterSelect label="Relocation" filterKey="relocation" optionsKey="relocation_options" />
          <FilterSelect label="Availability" filterKey="availablty" optionsKey="availability_options" />
          <FilterSelect label="Vendor" filterKey="vendorname" optionsKey="vendors" />
          <FilterSelect label="Condition" filterKey="condition" optionsKey="conditions" />
          <FilterSelect label="Hold" filterKey="hold1" optionsKey="hold_options" />
        </div>
      )}

      {/* Results Table */}
      <div className="card">
        <div className="table-wrapper">
          <table className="data-table">
            <thead><tr>
              <th className="checkbox-cell"><input type="checkbox" checked={selectAll} onChange={toggleSelectAll} /></th>
              <th onClick={() => toggleSort('fname')}>Name <ArrowUpDown size={11} style={{ display: 'inline', verticalAlign: 'middle', opacity: .5 }} /></th>
              <th onClick={() => toggleSort('employer')}>Employer</th>
              <th>Location</th>
              <th onClick={() => toggleSort('visastatus')}>Visa</th>
              <th>Rate</th>
              <th>Avail</th>
              <th>US Exp</th>
              <th>Reloc</th>
              <th>Email</th>
            </tr></thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={10}><div className="loading-spinner"><div className="spinner" /></div></td></tr>
              ) : resumes.length === 0 ? (
                  <tr><td colSpan={10}><div className="empty-state"><Search size={40} /><p>{query ? 'No candidates found. Adjust your search or filters.' : 'Enter a search term to find candidates.'}</p></div></td></tr>
                ) : resumes.map(r => (
                  <tr key={r.recno} style={{ cursor: 'pointer' }} onClick={(e) => { if (e.target.type !== 'checkbox') window.open(`/resumes/${r.recno}`, '_blank'); }}>
                    <td className="checkbox-cell" onClick={e => e.stopPropagation()}><input type="checkbox" checked={selected.has(r.recno)} onChange={() => toggleSelect(r.recno)} /></td>
                    <td><div className="candidate-cell"><div className="name">{(r.fname || '')} {(r.lname || '')}</div>{r.csgcomment && <div className="sub">{r.csgcomment}</div>}</div></td>
                    <td style={{ fontSize: '.85rem' }}>{r.employer || '—'}</td>
                    <td style={{ fontSize: '.84rem' }}>{r.location_display || r.location || '—'}</td>
                    <td>{r.visastatus ? <span className="badge badge-visa">{r.visastatus}</span> : '—'}</td>
                  <td style={{ fontFamily: 'var(--font-mono)', fontSize: '.84rem' }}>{r.rate ? `${r.rate}/${r.unit1 || 'hr'}` : '—'}</td>
                  <td style={{ fontSize: '.84rem' }}>{(r.availablty || '').trim() || '—'}</td>
                  <td style={{ fontSize: '.84rem' }}>{(r.usexp || '').trim() || '—'}</td>
                  <td style={{ fontSize: '.84rem' }}>{(r.relocation || '').trim() || '—'}</td>
                  <td style={{ fontSize: '.82rem', color: 'var(--accent)' }}>{r.email ? '✓' : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {totalPages > 1 && (
          <div className="pagination">
            <button disabled={page <= 1} onClick={() => setPage(p => p - 1)}>Previous</button>
            {Array.from({ length: Math.min(totalPages, 7) }, (_, i) => {
              let pn;
              if (totalPages <= 7) pn = i + 1;
              else if (page <= 4) pn = i + 1;
              else if (page >= totalPages - 3) pn = totalPages - 6 + i;
              else pn = page - 3 + i;
              return <button key={pn} className={page === pn ? 'active' : ''} onClick={() => setPage(pn)}>{pn}</button>;
            })}
            <span className="pagination-info">of {totalPages}</span>
            <button disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>Next</button>
          </div>
        )}
      </div>

      {/* Email Selected Modal */}
      <Modal isOpen={emailModal} onClose={() => setEmailModal(false)} title={`Email ${selected.size} Selected Candidate${selected.size > 1 ? 's' : ''}`} wide
        footer={<><button className="btn btn-secondary" onClick={() => setEmailModal(false)}>Cancel</button><button className="btn btn-primary" onClick={handleSendEmail} disabled={sendingEmail}><Mail size={16} /> {sendingEmail ? 'Sending...' : 'Send Email'}</button></>}>
        <div className="form-group"><label className="form-label">Subject</label><input className="form-input" value={emailSubject} onChange={e => setEmailSubject(e.target.value)} placeholder="Email subject line" /></div>
        <div className="form-group"><label className="form-label">Body</label><textarea className="form-textarea" value={emailBody} onChange={e => setEmailBody(e.target.value)} placeholder="Email body..." style={{ minHeight: 200 }} /></div>
        <p style={{ fontSize: '.8rem', color: 'var(--text-muted)' }}>Only candidates with sendmail=1 and a valid email will receive the message.</p>
      </Modal>

      {/* Email All Results Modal */}
      <Modal isOpen={emailAllModal} onClose={() => { setEmailAllModal(false); setAllEmailInfo(null); }} title="Email All Search Results" wide
        footer={<><button className="btn btn-secondary" onClick={() => { setEmailAllModal(false); setAllEmailInfo(null); }}>Cancel</button><button className="btn btn-primary" onClick={handleSendEmailAll} disabled={sendingEmail}><Send size={16} /> {sendingEmail ? 'Sending...' : `Send to ${allEmailInfo?.count || 0} Recipients`}</button></>}>
        <div style={{ background: 'var(--bg-elevated)', padding: '12px 16px', borderRadius: 'var(--radius)', marginBottom: 20, fontSize: '.88rem' }}>
          <strong style={{ color: 'var(--accent)' }}>{allEmailInfo?.count || 0}</strong> eligible recipients found across all {totalCount} search results
          <span style={{ color: 'var(--text-muted)', fontSize: '.8rem', display: 'block', marginTop: 4 }}>Only candidates with sendmail=1 and a valid email address</span>
        </div>
        <div className="form-group"><label className="form-label">Subject</label><input className="form-input" value={emailSubject} onChange={e => setEmailSubject(e.target.value)} placeholder="Email subject line" /></div>
        <div className="form-group"><label className="form-label">Body</label><textarea className="form-textarea" value={emailBody} onChange={e => setEmailBody(e.target.value)} placeholder="Email body..." style={{ minHeight: 200 }} /></div>
      </Modal>
    </div>
  );
}